import "./LearnPage.css";
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from "react";
import { useMathJax } from "../../hooks/useMathJax";
import { useHighlights } from "../../hooks/useHighlights";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import {
  // flattenFrames,
  // buildTOC,
  buildCommentsMap,
  countProblems,
  // formatTime,
  // buildProblemSetList,
  svgToDataUrl,
  parseArrowKey,
} from "../../utils/learnUtils";
import TOCPanel from "./TOCPanel";
import MainCanvas from "./MainCanvas";
import BookmarkModal from "./BookmarkModal";
import CommentsPanel from "./CommentsPanel";
import SupportPanel from "./SupportPanel";
import SupportSearch from "./SupportSearch";
import MultiFigureViewer from "./content/MultiFigureViewer";
import logoRect from "../../images/LogoRect.svg";
import { COMMENTS_WIDTH, TOC_WIDTH } from "../../constants/layoutConstants";
import { fetchCurrentContent } from "../../actions/content";
import {
  updateProgressAddBookmark,
  updateProgressEditBookmark,
} from "../../actions/progress";
import { updateUserCoursesTaken } from "../../actions/user";
import {
  SET_SUPPORT_STATE,
  STORE_CONTENT,
  SIGNAL_START_STOPWATCH,
} from "../../constants/actionConstants";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

/* ─── Context ───────────────────────────────────────────────── */
export const LearnCtx = createContext(null);
export function useLearnCtx() {
  return useContext(LearnCtx);
}

/**
 * Single-figure lightbox: image scaled to fill as much of the viewport as
 * possible (up to 90 % each axis), caption with MathJax.
 * Sizing is done in onLoad because CSS alone can't scale an <img> *up* to
 * a viewport fraction while keeping aspect ratio and a tight white container.
 * @param {{ svg: string, caption: string }} props
 */
function FigureLightboxContent({ svg, caption }) {
  const captionRef = useRef(null);
  useMathJax(captionRef, [caption]);
  const [imgStyle, setImgStyle] = useState(null);
  const [activeKeys, setActiveKeys] = useState(new Set());

  // 250 px per 1000 characters; minimum 60 px so a short caption isn't clipped
  const captionMaxH = caption
    ? Math.max(60, Math.round((caption.length / 1000) * 300))
    : 0;

  const handleLoad = (e) => {
    const { naturalWidth: nw, naturalHeight: nh } = e.target;
    if (!nw || !nh) return;
    const fs = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const innerPad = fs * 2.5; // 1.25 rem top + 1.25 rem bottom
    const captionOverhead = caption
      ? captionMaxH + fs // dynamic cap + 1 rem gap
      : 0;
    const maxW = window.innerWidth * 0.85 - innerPad;
    const maxH = window.innerHeight * 0.85 - innerPad - captionOverhead;
    const scale = Math.min(maxW / nw, maxH / nh);
    setImgStyle({
      width: `${Math.round(nw * scale)}px`,
      height: `${Math.round(nh * scale)}px`,
    });
  };

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    const onClick = (e) => {
      const btn = e.target.closest(".ct__fig-ptr-btn");
      if (!btn) return;
      e.preventDefault();
      const key = btn.dataset.arrows;
      setActiveKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    el.querySelectorAll(".ct__fig-ptr-btn").forEach((btn) => {
      btn.classList.toggle(
        "ct__fig-ptr-btn--active",
        activeKeys.has(btn.dataset.arrows),
      );
    });
  }, [activeKeys]);

  const visibleArrows = [...activeKeys].flatMap(parseArrowKey);

  return (
    <>
      <div className="learn__lightbox-img-wrap">
        <img
          src={svgToDataUrl(svg)}
          alt="Figure"
          className="learn__lightbox-img"
          style={imgStyle ?? undefined}
          onLoad={handleLoad}
        />
        {visibleArrows.map(({ x, y }, i) => (
          <svg
            key={i}
            className="ct__fig-ptr-arrow"
            style={{ left: `${x}%`, top: `${y}%` }}
            width="16"
            height="28"
            viewBox="0 0 16 28"
            aria-hidden="true"
          >
            <line
              x1="8"
              y1="0"
              x2="8"
              y2="17"
              stroke="white"
              strokeWidth="6"
              strokeLinecap="round"
            />
            <polygon points="8,28 0,14 16,14" fill="white" />
            <line
              x1="8"
              y1="1"
              x2="8"
              y2="17"
              stroke="var(--blue-primary)"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <polygon points="8,27 2,15 14,15" fill="var(--blue-primary)" />
          </svg>
        ))}
      </div>
      {caption && (
        <p
          ref={captionRef}
          className="learn__lightbox-caption"
          style={{ maxHeight: `${captionMaxH}px` }}
          dangerouslySetInnerHTML={{ __html: caption }}
        />
      )}
    </>
  );
}

/** @returns {JSX.Element} */
export default function LearnPage() {
  const canvasRef = useRef(null);

  /* ── Read / active tracking ── */
  const [readFrames, setReadFrames] = useState(new Set());
  const [activeFrameId, setActiveFrameId] = useState(null);

  /* ── Problem tracking ── */
  const [answeredProblems, setAnsweredProblems] = useState(new Set());
  const [openProblemSetId, setOpenProblemSetId] = useState(null);
  const [currentProblemIdx, setCurrentProblemIdx] = useState({});

  /* ── Frame side panels (Q / Notes / Eq) ── */
  // { frameId, type: 'question'|'equation', contentId? }  — notes are tracked separately
  const [openFramePanel, setOpenFramePanel] = useState(null);
  // const [notesContent, setNotesContent] = useState({}); // frameId → markdown
  // Set of frameIds whose notes panel is currently open (multiple allowed simultaneously)
  const [openNotesPanels, setOpenNotesPanels] = useState(new Set());
  // Last known open set — restored when the user reopens any notes panel after a global close
  const savedNotesPanelsRef = useRef(new Set());

  /* ── Right Comments panel ── */
  const [commentsFrameId, setCommentsFrameId] = useState(null);
  const [commentsItemId, setCommentsItemId] = useState(null);

  /* ── Support panel ── */
  const [supportState, setSupportState] = useState({
    itemId: null,
    history: [],
    historyIdx: -1,
  });

  /* ── Mobile overlay panels ── */
  const [tocOverlay, setTocOverlay] = useState(false);
  const [commentsOverlay, setCommentsOverlay] = useState(false);

  /* ── Narrow-mode right-panel state (≤ 640 px) ── */
  // narrowSupportOpen: support panel is open even when no item is selected
  // (so the user sees just the embedded search bar).
  const [narrowSupportOpen, setNarrowSupportOpen] = useState(false);
  // savedCommentsFrameId: remembers the last comments frame so mob-right can
  // reopen it.  Cleared only when the user explicitly closes via the ✕ button.
  const [savedCommentsFrameId, setSavedCommentsFrameId] = useState(null);

  /* ── Timer ── */
  const studySecondsRef = useRef(0);
  const lastInteractionRef = useRef(null);
  const lastSignalTimeRef = useRef(0);

  /* ── Figure lightbox ── */
  const [lightbox, setLightbox] = useState(null); // { svg, caption }
  const lightboxTriggerRef = useRef(null);
  const lightboxInnerRef = useRef(null);

  /* ── Highlights ── */
  const { highlights, saveHighlight, deleteHighlight } = useHighlights();
  const [highlightColor, setHighlightColor] = useState("yellow");

  /* ── Bookmarks ── */
  const [bookmarkModal, setBookmarkModal] = useState(null); // null | { contentId, defaultName }
  const [editBookmarkModal, setEditBookmarkModal] = useState(null); // null | { bookmark }

  const openLightbox = useCallback((data) => {
    if (data) lightboxTriggerRef.current = document.activeElement;
    setLightbox(data);
  }, []);

  const openBookmarkModal = useCallback((contentId, defaultName, scrollId) => {
    setBookmarkModal({
      contentId,
      defaultName: defaultName ?? "",
      scrollId: scrollId ?? "",
    });
  }, []);

  const openEditBookmarkModal = useCallback((bookmark) => {
    setEditBookmarkModal({ bookmark });
  }, []);

  // Get dispatch and navigate objects
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Get the post id from the URL
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  /* ── Interaction tracking → reset timer window and signal stopwatch ── */
  const onInteraction = useCallback(() => {
    const now = Date.now();
    lastInteractionRef.current = now;
    if (now - lastSignalTimeRef.current >= 1000) {
      lastSignalTimeRef.current = now;
      dispatch({ type: SIGNAL_START_STOPWATCH, payload: Math.random() });
    }
  }, [dispatch]);

  const addBookmark = useCallback(
    (bookmark) => {
      dispatch(updateProgressAddBookmark(id, bookmark));
    },
    [dispatch, id],
  );

  const editBookmark = useCallback(
    (bookmark) => {
      dispatch(updateProgressEditBookmark(id, bookmark));
    },
    [dispatch, id],
  );
  // Load main content
  useEffect(() => {
    // Retrieve user details
    dispatch(fetchCurrentContent(id, navigate));
    dispatch({ type: SET_SUPPORT_STATE, payload: null });
    // Delete the content when leave this page so table of contents for old content not still there
    return () => {
      dispatch({ type: STORE_CONTENT, payload: undefined });
    };
  }, [id, dispatch, navigate]);

  // Retrieve content from reducer
  // const rawChapters = useSelector((state) => state.ContentReducer.content);
  const rawContent = useSelector((state) => state.ContentReducer.content);

  // Add this course to the user record the first time they open it. Waits for
  // the content to load so an invalid id (which redirects home) creates nothing.
  // The ref stops a second dispatch before the server reply updates localStorage.
  const courseRecordedRef = useRef(null);
  useEffect(() => {
    if (!rawContent || courseRecordedRef.current === id) return;
    courseRecordedRef.current = id;
    const user = JSON.parse(localStorage.getItem("profile"));
    if (!user) return;
    const alreadyTakingCourse = user.result?.coursesTaken?.some(
      (courseTaken) => courseTaken.courseID === id,
    );
    if (!alreadyTakingCourse) {
      const courseTaken = {
        courseID: id,
        completed: false,
        dateLastSeen: Date.now(),
        elapsedTime: 0,
        percentComplete: 0,
      };
      dispatch(updateUserCoursesTaken(id, courseTaken));
    }
  }, [rawContent, id, dispatch]);

  // Scroll to a specific bookmark when arriving from the bookmarks page
  useEffect(() => {
    const scrollTo = searchParams.get("scrollTo");
    if (!scrollTo || !rawContent?.length) return;
    const attempt = (tries) => {
      const el = document.getElementById(scrollTo);
      if (el) {
        const container = document.querySelector(".learn__canvas-panel");
        if (container) {
          const top =
            container.scrollTop +
            el.getBoundingClientRect().top -
            container.getBoundingClientRect().top -
            container.clientHeight * 0.2;
          container.scrollTo({ top, behavior: "smooth" });
        } else {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        el.classList.add("toc__bm-item-highlight");
        setTimeout(() => el.classList.remove("toc__bm-item-highlight"), 2000);
      } else if (tries > 0) {
        setTimeout(() => attempt(tries - 1), 300);
      }
    };
    setTimeout(() => attempt(6), 400);
  }, [searchParams, rawContent]);

  const rawComments = useSelector((state) => state.CommentReducer.comments);
  const rawSupport = useSelector((state) => state.SupportReducer.supportItems);

  /* ─── Derived data (stable across renders) ──────────────────── */
  // const frames = useMemo(() => flattenFrames(rawChapters), [rawChapters]);
  // const tocItems = buildTOC(rawChapters);
  const commentsMap = useMemo(
    () => buildCommentsMap(rawComments),
    [rawComments],
  );
  // const totalProblems = useMemo(
  //   () => countProblems(rawChapters),
  //   [rawChapters],
  // );
  // const problemSets = buildProblemSetList(rawChapters);

  useEffect(() => {
    const events = ["scroll", "click", "keydown", "pointerdown"];
    events.forEach((e) =>
      window.addEventListener(e, onInteraction, { passive: true }),
    );
    return () =>
      events.forEach((e) => window.removeEventListener(e, onInteraction));
  }, [onInteraction]);

  /* ── Lightbox: focus trap + restore focus on close ── */
  useFocusTrap(lightboxInnerRef, !!lightbox);
  useEffect(() => {
    if (!lightbox) lightboxTriggerRef.current?.focus();
  }, [lightbox]);

  /* ── Close narrow-only state when viewport widens past 640 px ── */
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const handler = (e) => {
      if (!e.matches) setNarrowSupportOpen(false);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  /* ── Timer interval ── */
  useEffect(() => {
    const id = setInterval(() => {
      if (
        lastInteractionRef.current &&
        Date.now() - lastInteractionRef.current < 60_000
      ) {
        studySecondsRef.current += 1;
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  /* ── Mark frame read (called from MainCanvas on long visibility) ── */
  const markRead = useCallback((frameId) => {
    setReadFrames((prev) => {
      if (prev.has(frameId)) return prev;
      const next = new Set(prev);
      next.add(frameId);
      return next;
    });
  }, []);

  /* ── Notes panels — multiple can be open simultaneously ─────────── */

  /**
   * Close every open notes panel and save the set so it can be restored
   * the next time any notes panel is opened.  Called when Q/Eq/Comments opens.
   */
  const closeAllNotes = useCallback(() => {
    setOpenNotesPanels((prev) => {
      if (prev.size > 0) savedNotesPanelsRef.current = new Set(prev);
      return new Set();
    });
  }, []);

  /**
   * Open (or toggle off) the notes panel for `frameId`.
   * If all notes were previously globally closed, the saved set is restored
   * together with this frame's panel.
   */
  const openNotes = useCallback((frameId) => {
    // Close any open Q/Eq panel (same rule as today — notes replaces it)
    setOpenFramePanel(null);
    setOpenNotesPanels((prev) => {
      if (prev.has(frameId)) {
        // Toggle off — close just this frame's notes
        const next = new Set(prev);
        next.delete(frameId);
        return next;
      }
      if (prev.size === 0 && savedNotesPanelsRef.current.size > 0) {
        // Restore previously-open set and add this frame
        const next = new Set(savedNotesPanelsRef.current);
        next.add(frameId);
        savedNotesPanelsRef.current = new Set();
        return next;
      }
      // Simply add this frame to the open set
      const next = new Set(prev);
      next.add(frameId);
      return next;
    });
    // At narrow viewport, close the right panel (same rule as Q/Eq)
    if (!window.matchMedia("(min-width: 2225px)").matches)
      setCommentsFrameId(null);
  }, []);

  /**
   * Close the notes panel for `frameId` only (explicit ✕ click on the panel).
   * Also removes it from the saved set so it won't be auto-restored.
   */
  const closeNotes = useCallback((frameId) => {
    setOpenNotesPanels((prev) => {
      if (!prev.has(frameId)) return prev;
      const next = new Set(prev);
      next.delete(frameId);
      return next;
    });
    if (savedNotesPanelsRef.current.has(frameId)) {
      const next = new Set(savedNotesPanelsRef.current);
      next.delete(frameId);
      savedNotesPanelsRef.current = next;
    }
  }, []);

  /* ── Open a frame panel (Q/Eq) — only one at a time ── */
  const openPanel = useCallback(
    (frameId, type, contentId = null) => {
      setOpenFramePanel((prev) => {
        if (
          prev?.frameId === frameId &&
          prev?.type === type &&
          prev?.contentId === contentId
        ) {
          return null; // toggle off
        }
        return { frameId, type, contentId };
      });
      // Opening Q/Eq → close all notes panels (saved for restore)
      closeAllNotes();
      // Close right comments panel when a frame panel opens — but not at ≥2225px
      // where there is enough room for both panels to coexist.
      if (!window.matchMedia("(min-width: 2225px)").matches) {
        setCommentsFrameId(null);
      }
    },
    [closeAllNotes],
  );

  const closeFramePanel = useCallback(() => setOpenFramePanel(null), []);

  /* ── Open Comments panel (right) ── */
  const openComments = useCallback(
    (frameId, itemId = null) => {
      setCommentsFrameId(frameId);
      setCommentsItemId(itemId);
      // Close frame panels (EqTooltip / Notes / QuestionDialog) when opening
      // comments — but not at ≥2225px where there is room for both.
      if (!window.matchMedia("(min-width: 2225px)").matches) {
        setOpenFramePanel(null);
        closeAllNotes();
      }
      setSupportState((prev) => ({ ...prev, itemId: null }));
      setNarrowSupportOpen(false);
      // At ≤1024px: TOC and right panel are mutually exclusive
      if (window.matchMedia("(max-width: 1024px)").matches)
        setTocOverlay(false);
    },
    [closeAllNotes],
  );

  /** Plain close — used internally (does NOT reset narrow mode). */
  const closeComments = useCallback(() => setCommentsFrameId(null), []);

  /**
   * Explicit close via the CommentsPanel ✕ button.
   * Also clears the saved frame so mob-right reverts to support/📖 mode.
   */
  const closeCommentsExplicit = useCallback(() => {
    setCommentsFrameId(null);
    setSavedCommentsFrameId(null);
  }, []);

  /* ── Support panel ── */
  const openSupport = useCallback(
    (id) => {
      setSupportState((prev) => {
        const trimmed = prev.history.slice(0, prev.historyIdx + 1);
        // Skip push if already showing this item — prevents duplicate entries
        if (trimmed.length > 0 && trimmed[trimmed.length - 1] === id) {
          return { ...prev, itemId: id };
        }
        const newHistory = [...trimmed, id];
        return {
          itemId: id,
          history: newHistory,
          historyIdx: newHistory.length - 1,
        };
      });
      setCommentsFrameId(null);
      // Close frame panels (EqTooltip / Notes / QuestionDialog) when opening
      // support — but not at ≥2225px where there is room for both.
      if (!window.matchMedia("(min-width: 2225px)").matches) {
        setOpenFramePanel(null);
        closeAllNotes();
      }
      setCommentsOverlay(false);
      setNarrowSupportOpen(true);
      // At ≤1024px: TOC and right panel are mutually exclusive
      if (window.matchMedia("(max-width: 1024px)").matches)
        setTocOverlay(false);
    },
    [closeAllNotes],
  );

  const closeSupport = useCallback(() => {
    setSupportState((prev) => ({ ...prev, itemId: null }));
  }, []);

  /* ── Escape key: close the topmost open panel/modal ── */
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== "Escape") return;
      if (lightbox) {
        setLightbox(null);
        return;
      }
      if (bookmarkModal) {
        setBookmarkModal(null);
        return;
      }
      if (editBookmarkModal) {
        setEditBookmarkModal(null);
        return;
      }
      if (openNotesPanels.size > 0) {
        closeAllNotes();
        return;
      }
      if (openFramePanel) {
        closeFramePanel();
        return;
      }
      if (commentsOverlay) {
        setCommentsOverlay(false);
        return;
      }
      if (commentsFrameId) {
        closeCommentsExplicit();
        return;
      }
      if (narrowSupportOpen) {
        closeSupport();
        setNarrowSupportOpen(false);
        return;
      }
      if (tocOverlay) {
        setTocOverlay(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [
    lightbox,
    bookmarkModal,
    editBookmarkModal,
    openNotesPanels,
    openFramePanel,
    commentsOverlay,
    commentsFrameId,
    narrowSupportOpen,
    tocOverlay,
    closeAllNotes,
    closeFramePanel,
    closeCommentsExplicit,
    closeSupport,
  ]);

  const supportBack = useCallback(() => {
    setSupportState((prev) => {
      if (prev.historyIdx <= 0) return prev;
      const newIdx = prev.historyIdx - 1;
      return { ...prev, itemId: prev.history[newIdx], historyIdx: newIdx };
    });
  }, []);

  const supportForward = useCallback(() => {
    setSupportState((prev) => {
      if (prev.historyIdx >= prev.history.length - 1) return prev;
      const newIdx = prev.historyIdx + 1;
      return { ...prev, itemId: prev.history[newIdx], historyIdx: newIdx };
    });
  }, []);

  /* ── Canvas click: intercept support-link anchors; close right panel otherwise ── */
  const handleCanvasClick = useCallback(
    (e) => {
      // Support-link anchor → open support panel
      const link = e.target.closest("[data-support-id],[data-support-label]");
      if (link) {
        e.preventDefault();
        if (link.dataset.supportId) {
          openSupport(link.dataset.supportId);
        } else if (link.dataset.supportLabel) {
          // Figure/equation ref links store a label; look up the ref record by label
          const rec = rawSupport.find(
            (r) => r.label === link.dataset.supportLabel,
          );
          if (rec) openSupport(rec._id);
        }
        return;
      }
      // Interacting with a frame side panel — don't dismiss the right panel
      if (e.target.closest(".frame__panel")) return;
      // At ≥2225px clicking a frame icon (notes / question / equation) opens a panel
      // alongside the right panel — don't close comments/support in that case.
      if (
        window.matchMedia("(min-width: 2225px)").matches &&
        e.target.closest(".frame__icons")
      )
        return;
      // Clicking multifigure interactive controls (prev/next arrows, expand button,
      // progress segments) — the user is navigating a figure, not dismissing anything.
      if (
        e.target.closest(
          ".ct__mfig-nav, .ct__figure-expand, .ct__mfig-progress",
        )
      )
        return;
      // At ≥1025px the right panel sits alongside the canvas — don't dismiss it.
      if (window.matchMedia("(min-width: 1025px)").matches) return;
      // Any other canvas click → close the right (comments / support) panel
      setCommentsFrameId(null);
      setCommentsOverlay(false);
      setNarrowSupportOpen(false);
    },
    [openSupport, rawSupport],
  );

  /* ── Problem set toggling (one at a time) ── */
  const toggleProblemSet = useCallback((setId) => {
    setOpenProblemSetId((prev) => (prev === setId ? null : setId));
    setCurrentProblemIdx((prev) => ({ ...prev, [setId]: prev[setId] ?? 0 }));
  }, []);

  /* ── Open a specific problem set at a specific index ── */
  const openProblemSet = useCallback((setId, problemIdx) => {
    setOpenProblemSetId(setId);
    setCurrentProblemIdx((prev) => ({ ...prev, [setId]: problemIdx }));
  }, []);

  const markProblemAnswered = useCallback((problemId) => {
    setAnsweredProblems((prev) => {
      const next = new Set(prev);
      next.add(problemId);
      return next;
    });
  }, []);

  /* ── Narrow mob-right button: support ↔ comments toggling ── */
  const handleMobRightClick = useCallback(() => {
    if (!window.matchMedia("(max-width: 1024px)").matches) {
      // > 640 px: existing behaviour — toggle comments overlay
      setCommentsOverlay((v) => !v);
      return;
    }
    // ≤ 640 px state machine
    const commentsVisible = commentsFrameId !== null;
    // narrowSupportOpen is the sole visibility flag for support; itemId is
    // intentionally kept so the panel can be restored on the next press.
    const supportVisible = narrowSupportOpen;

    if (commentsVisible) {
      // Close comments; remember the frame so the button can reopen it
      setSavedCommentsFrameId(commentsFrameId);
      closeComments();
    } else if (supportVisible) {
      // Collapse support panel — keep itemId so content is restored on reopen
      setNarrowSupportOpen(false);
    } else if (savedCommentsFrameId !== null) {
      // Reopen the last comments thread
      openComments(savedCommentsFrameId);
    } else {
      // Default: open the support panel (shows retained item or just search bar)
      setNarrowSupportOpen(true);
      setTocOverlay(false); // mutually exclusive with TOC at ≤1024px
    }
  }, [
    commentsFrameId,
    narrowSupportOpen,
    savedCommentsFrameId,
    closeComments,
    openComments,
  ]);

  const hasComments = commentsFrameId !== null;
  // narrowSupportOpen is the sole visibility gate for the support panel;
  // supportState.itemId may be non-null even when the panel is closed (retained for restore).
  const rightPanelOpen = hasComments || commentsOverlay || narrowSupportOpen;

  /* ── Mob-right button mode ── */
  // CSS controls which icon (📖 vs 💬) is shown at each breakpoint.
  // narrowCommentsMode drives the --comments-mode modifier so CSS can
  // show 💬 at ≤1024px when a thread is active/saved (otherwise 📖).
  const narrowCommentsMode =
    commentsFrameId !== null || savedCommentsFrameId !== null;

  const ctx = useMemo(
    () => ({
      /* data */
      // frames,
      // tocItems,
      commentsMap,
      // problemSets,
      // totalProblems,
      /* canvas */
      canvasRef,
      /* read tracking */
      readFrames,
      markRead,
      activeFrameId,
      setActiveFrameId,
      /* frame panels */
      openFramePanel,
      openPanel,
      closeFramePanel,
      /* notes panels (multiple open simultaneously) */
      openNotesPanels,
      openNotes,
      closeNotes,
      // notesContent,
      // setNotesContent,
      /* comments */
      commentsFrameId,
      commentsItemId,
      openComments,
      closeComments,
      /* support panel */
      supportItemId: supportState.itemId,
      supportHistory: supportState.history,
      supportHistoryIdx: supportState.historyIdx,
      canSupportBack: supportState.historyIdx > 0,
      canSupportForward:
        supportState.historyIdx < supportState.history.length - 1,
      openSupport,
      closeSupport,
      supportBack,
      supportForward,
      /* lightbox */
      lightbox,
      setLightbox: openLightbox,
      /* highlights */
      highlights,
      saveHighlight,
      deleteHighlight,
      highlightColor,
      setHighlightColor,
      /* bookmarks */
      openBookmarkModal,
      openEditBookmarkModal,
      addBookmark,
      editBookmark,
      /* logo */
      logoRect,
    }),
    [
      // frames,
      commentsMap,
      // totalProblems,
      readFrames,
      markRead,
      activeFrameId,
      setActiveFrameId,
      openFramePanel,
      openPanel,
      closeFramePanel,
      openNotesPanels,
      openNotes,
      closeNotes,
      // notesContent,
      // setNotesContent,
      commentsFrameId,
      commentsItemId,
      openComments,
      closeComments,
      supportState,
      openSupport,
      closeSupport,
      supportBack,
      supportForward,
      lightbox,
      openLightbox,
      highlights,
      saveHighlight,
      deleteHighlight,
      highlightColor,
      setHighlightColor,
      openBookmarkModal,
      openEditBookmarkModal,
      addBookmark,
      editBookmark,
    ],
  );

  return (
    <LearnCtx.Provider value={ctx}>
      <div
        className="learn"
        onPointerDown={onInteraction}
        onScroll={onInteraction}
        style={{
          "--toc-width": `${TOC_WIDTH}px`,
          "--comments-width": `${COMMENTS_WIDTH}px`,
        }}
      >
        {/* ── Mobile TOC trigger ── */}
        <button
          className={`learn__mob-btn learn__mob-btn--left${tocOverlay ? " learn__mob-btn--hidden" : ""}`}
          onClick={() => {
            const opening = !tocOverlay;
            setTocOverlay(opening);
            // At ≤1024px: opening the TOC closes the right panel (mutually exclusive)
            if (opening && window.matchMedia("(max-width: 1024px)").matches) {
              setNarrowSupportOpen(false);
              setCommentsFrameId(null);
            }
          }}
          aria-label="Table of contents"
        >
          <span className="learn__mob-icon">☰</span>
        </button>

        {/* ── Mobile right-panel trigger ── */}
        <button
          className={`learn__mob-btn learn__mob-btn--right${narrowCommentsMode ? " learn__mob-btn--comments-mode" : ""}${rightPanelOpen ? " learn__mob-btn--hidden" : ""}`}
          onClick={handleMobRightClick}
          aria-label={narrowCommentsMode ? "Comments" : "Support topics"}
        >
          {/* CSS selects which icon is visible per breakpoint + mode */}
          <span className="learn__mob-icon learn__mob-icon--support">📖</span>
          <span className="learn__mob-icon learn__mob-icon--comments">💬</span>
        </button>

        {/* ── Left Panel: TOC ── */}
        <aside
          className={`learn__toc-panel${tocOverlay ? " learn__toc-panel--open" : ""}`}
        >
          <TOCPanel onClose={() => setTocOverlay(false)} />
        </aside>

        {/* TOC overlay backdrop */}
        {tocOverlay && (
          <div
            className="learn__overlay-backdrop"
            onClick={() => setTocOverlay(false)}
          />
        )}

        {/* ── Center Panel: Main Canvas ── */}
        <main
          className="learn__canvas-panel"
          ref={canvasRef}
          onClick={handleCanvasClick}
        >
          <MainCanvas />
        </main>

        {/* ── Right Panel: Comments or Support (shared slot) ── */}
        <aside
          className={`learn__comments-panel${rightPanelOpen ? " learn__comments-panel--open" : ""}`}
        >
          {hasComments || commentsOverlay ? (
            <CommentsPanel
              onClose={() => {
                closeCommentsExplicit();
                setCommentsOverlay(false);
              }}
            />
          ) : narrowSupportOpen ? (
            <SupportPanel
              onClose={() => {
                closeSupport();
                setNarrowSupportOpen(false);
              }}
              showSearch={narrowSupportOpen}
            />
          ) : null}
        </aside>

        {/* ── Support search (shown when right panel is closed) ── */}
        <div
          className={`learn__support-search${!rightPanelOpen ? " learn__support-search--visible" : ""}`}
        >
          <SupportSearch />
        </div>

        {/* Comments overlay backdrop */}
        {commentsOverlay && (
          <div
            className="learn__overlay-backdrop"
            onClick={() => setCommentsOverlay(false)}
          />
        )}

        {/* Right-panel backdrop — narrow (≤1024px) config only, CSS hides it above that */}
        {rightPanelOpen && (
          <div
            className="learn__overlay-backdrop learn__overlay-backdrop--narrow-right"
            onClick={() => {
              setNarrowSupportOpen(false);
              setCommentsFrameId(null);
            }}
          />
        )}

        {/* ── Bookmark modal (add) ── */}
        {bookmarkModal && (
          <BookmarkModal
            defaultName={bookmarkModal.defaultName}
            contentId={bookmarkModal.contentId}
            scrollId={bookmarkModal.scrollId}
            onAdd={addBookmark}
            onClose={() => setBookmarkModal(null)}
          />
        )}

        {/* ── Bookmark modal (edit) ── */}
        {editBookmarkModal && (
          <BookmarkModal
            defaultName={editBookmarkModal.bookmark.name}
            defaultDescription={editBookmarkModal.bookmark.description}
            contentId={editBookmarkModal.bookmark.itemId}
            onSave={(updated) =>
              editBookmark({ ...editBookmarkModal.bookmark, ...updated })
            }
            onClose={() => setEditBookmarkModal(null)}
          />
        )}

        {/* ── Figure lightbox ── */}
        {/* lightbox shape:
              single figure  → { svg, caption }
              multi-figure   → { images, captions, initialIdx } */}
        {lightbox && (
          <div
            className="learn__lightbox"
            onClick={() => setLightbox(null)}
            role="dialog"
            aria-modal="true"
          >
            <div
              ref={lightboxInnerRef}
              className={`learn__lightbox-inner${lightbox.images ? " learn__lightbox-inner--multi" : ""}`}
              onClick={(e) => {
                const link = e.target.closest("[data-support-id]");
                if (link) {
                  e.preventDefault();
                  setLightbox(null);
                  openSupport(link.dataset.supportId);
                  return;
                }
                e.stopPropagation();
              }}
              style={
                lightbox.expandedWidth
                  ? { maxWidth: `min(${lightbox.expandedWidth}px, 92vw)` }
                  : undefined
              }
            >
              <button
                className="learn__lightbox-close"
                onClick={() => setLightbox(null)}
                aria-label="Close"
              >
                ✕
              </button>
              {lightbox.images ? (
                /* Multi-figure: navigable viewer with prev/next + progress bar */
                <MultiFigureViewer
                  images={lightbox.images}
                  captions={lightbox.captions}
                  initialIdx={lightbox.initialIdx ?? 0}
                  captionMaxH={Math.max(
                    80,
                    Math.round(
                      (Math.max(
                        ...lightbox.captions.map((c) => (c ?? "").length),
                      ) /
                        1000) *
                        300,
                    ),
                  )}
                  backgroundImage={lightbox.backgroundImage}
                />
              ) : (
                <FigureLightboxContent
                  svg={lightbox.svg}
                  caption={lightbox.caption}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </LearnCtx.Provider>
  );
}
